// One process map: the swimlane editor, its outline and the SOP it writes.
//
// The draft saves itself as you work. Lanes are who does the work: a role, or an AI agent. A scenario is a path
// through the map. Releasing is the one approval: it makes the draft the revision in effect, which people and agents
// follow, and files it in document control. Without edit access the map opens to read and comment on.
import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useReducer, useRef, useState, type ReactNode } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import {
  AREAS, DOC_NO, FIELD_LABEL, SYSTEM_LABEL, byId, deleteStep as dropStep, diffMaps, doneBy, fixChoices, ins, nextRev, outs, pathOf, problemsOf,
  slugId, startId, summaryOf, wiNumber, type Lane, type MapBody, type Scenario, type Step, type SystemKind,
} from "@couragemfg/api/processes-model";
import { CommandBar } from "../Shell";
import { ErrorBox } from "../ui";
import { openChat, setPageChat } from "../pageChat";
import { ApiError } from "../api";
import { can, useAccess } from "../security/store";
import { layout, sz } from "./layout";
import { MapCanvas, sysText, type Active } from "./MapCanvas";
import { OpenScreen, ScreenText } from "./OpenScreen";
import { screenNames, screenTarget } from "./screens";
import { SopView } from "./SopView";
import { RunList, RunStart, RunView, agentPlan } from "./RunPanel";
import { fmtDate, processes, type Meta, type ProcessDetail, type Run } from "./processApi";
import { StepStarts, useStartFromMap } from "./StartFromMap";
import "./processes.css";

const clone = <T,>(x: T): T => JSON.parse(JSON.stringify(x));
const narrow = () => window.matchMedia("(max-width: 900px)").matches;
const reduced = () => window.matchMedia("(prefers-reduced-motion: reduce)").matches;
const blankStep = (id: string, name: string, type: Step["type"], lane: string, owner = ""): Step => ({ id, name, type, lane, system: "manual", screen: "", owner, instruction: "", kpi: "", control: "" });
const nextLabel = (o: { label: string }[]) => { const used = o.map((f) => f.label); return !used.includes("Yes") ? "Yes" : !used.includes("No") ? "No" : `Option ${o.length + 1}`; };
const CHAT_PROMPTS = ["Add a credit check for new customers before release", "Add a final inspection after the build step", "Which steps could an agent do?"];
/** A lane as people read it in a list: agent lanes say so, unless the name already does. */
export const laneLabel = (l: Lane) => (l.agent && !/\bagents?\b/i.test(l.name) ? `${l.name} (agent)` : l.name);

type View = "map" | "outline" | "sop" | "runs";
type RunPanelState = { mode: "new" } | { mode: "view"; number: string; run?: Run | null } | null;
type SaveState = { state: "saved" | "saving" | "dirty" | "error" | "stale"; msg?: string; current?: ProcessDetail | null };
type Toast = { msg: string; action?: { label: string; fn: () => void } } | null;

// Text fields: one undo step per field you type in, not one per keystroke. A required field (a name) left blank goes
// back to what it was, in the same undo step: the server would otherwise store a stand-in the screen doesn't show.
const EditCtx = createContext<{ ro: boolean; begin: () => void; end: () => void }>({ ro: true, begin: () => {}, end: () => {} });
function TIn({ value, onText, className, required, ...rest }: { value: string; onText: (v: string) => void; className?: string; required?: boolean; maxLength?: number; "aria-label"?: string; placeholder?: string; id?: string; list?: string }) {
  const e = useContext(EditCtx);
  const was = useRef(value);
  return <input type="text" className={className} value={value} readOnly={e.ro} onChange={(x) => onText(x.target.value)} {...rest}
    onFocus={() => { was.current = value; e.begin(); }}
    onBlur={() => { if (required && !e.ro && !value.trim()) onText(was.current.trim() || "Untitled"); e.end(); }} />;
}
function TArea({ value, onText, ...rest }: { value: string; onText: (v: string) => void; maxLength?: number; "aria-label"?: string; placeholder?: string; rows?: number }) {
  const e = useContext(EditCtx);
  return <textarea value={value} readOnly={e.ro} onFocus={e.begin} onBlur={e.end} onChange={(x) => onText(x.target.value)} {...rest} />;
}

export function ProcessPage() {
  const { id = "" } = useParams();
  const [d, setD] = useState<ProcessDetail | null>(null);
  const [error, setError] = useState<unknown>(null);
  useEffect(() => { setD(null); setError(null); processes.get(id).then(setD, setError); }, [id]);
  if (error) return <div className="page"><ErrorBox error={error} /><Link to="/processes">All process maps</Link></div>;
  if (!d) return <div className="page"><div className="empty">Loading the map…</div></div>;
  return <Editor key={d.id} initial={d} />;
}

function Editor({ initial }: { initial: ProcessDetail }) {
  const access = useAccess();
  const noEdit = !can(access, "process-maps", "edit") || !!access?.viewAs?.readOnly;
  // People who can't edit see the revision in effect: that's how the work is done until the draft is released.
  const [showRel, setShowRel] = useState(() => noEdit && !!initial.released);
  useEffect(() => { if (noEdit && initial.released) setShowRel(true); }, [noEdit, initial.released]); // access can load after the map
  const [d, setD] = useState(initial); // what the server holds besides the draft: meta, the revision in effect, history, comments
  const docRef = useRef<MapBody>(clone(initial.draft));
  const [, bump] = useReducer((x: number) => x + 1, 0);
  const draftDoc = docRef.current;
  const doc: MapBody = showRel && d.released ? d.released : draftDoc; // what's on screen
  const ro = noEdit || showRel;
  // While a release is on its way, the map holds still, so what's released is what's on screen.
  const [busy, setBusyState] = useState<string | null>(null);
  const busyRef = useRef<string | null>(null);
  const setBusy = (b: string | null) => { busyRef.current = b; setBusyState(b); };
  const locked = () => ro || !!busyRef.current;
  const undoS = useRef<string[]>([]), redoS = useRef<string[]>([]);
  const typing = useRef<{ snap: string; pushed: boolean } | null>(null);

  // ----- saving: a moment after each change, with a version check -----
  const ver = useRef(initial.version), meta = useRef<Meta>({});
  const dirty = useRef(false), inflight = useRef<Promise<void> | null>(null), timer = useRef<number | undefined>(undefined);
  const [save, setSaveState] = useState<SaveState>({ state: "saved" });
  const saveRef = useRef(save); saveRef.current = save;
  const setSave = (s: SaveState) => { saveRef.current = s; setSaveState(s); };
  const doSave = async (): Promise<void> => {
    while (inflight.current) await inflight.current;
    if (!dirty.current || noEdit) return;
    dirty.current = false;
    // A name being typed or a half-typed document number waits; the draft saves anyway.
    const sentMeta: Meta = {}, held: Meta = {};
    for (const [k, v] of Object.entries(meta.current) as [keyof Meta, string | null][]) {
      if ((k === "title" && !String(v ?? "").trim()) || (k === "docNo" && !DOC_NO.test(String(v ?? "")))) (held as any)[k] = v; else (sentMeta as any)[k] = v;
    }
    meta.current = held;
    setSave({ state: "saving" });
    const run = processes.save(d.id, ver.current, { draft: docRef.current, ...sentMeta }).then(
      (r) => {
        ver.current = r.version;
        if (sentMeta.docNo !== undefined) savedDocNo.current = sentMeta.docNo!;
        if (!dirty.current) dropBackup();
        setSave(dirty.current ? { state: "dirty" } : { state: "saved" }); setMetaErr(null);
      },
      (e) => {
        dirty.current = true;
        if (e instanceof ApiError && e.status === 409 && e.body?.code === "stale") { meta.current = { ...sentMeta, ...meta.current }; setSave({ state: "stale", msg: e.message, current: e.body.current ?? null }); keepBackup(); return; }
        // A document number someone else has: keep the old one and save the rest.
        if (sentMeta.docNo !== undefined) { const { docNo: _, ...rest } = sentMeta; meta.current = { ...rest, ...meta.current }; setMetaErr(e instanceof Error ? e.message : String(e)); setD((x) => ({ ...x, docNo: savedDocNo.current })); setSave({ state: "dirty" }); return; }
        meta.current = { ...sentMeta, ...meta.current };
        setSave({ state: "error", msg: e instanceof Error ? e.message : String(e) }); keepBackup();
      },
    ).finally(() => { inflight.current = null; });
    inflight.current = run;
    await run;
    if (dirty.current && saveRef.current.state === "dirty") schedule();
  };
  const schedule = () => { window.clearTimeout(timer.current); timer.current = window.setTimeout(() => { void doSave(); }, 700); };
  const markDirty = () => { dirty.current = true; if (saveRef.current.state !== "stale") { setSave({ state: "dirty" }); schedule(); } };
  /** Save now; throws when the save can't go through (someone else saved, or the API refused it). */
  const flush = async () => {
    window.clearTimeout(timer.current);
    await doSave();
    const s = saveRef.current;
    if (s.state === "stale" || s.state === "error") throw new Error(s.msg ?? "The map isn't saved.");
  };
  const [metaErr, setMetaErr] = useState<string | null>(null);
  const savedDocNo = useRef(initial.docNo);
  // Changes that couldn't be saved are kept in this browser, so leaving the page doesn't lose them.
  const backupKey = `pmap-unsaved:${initial.id}`;
  const keepBackup = () => { try { localStorage.setItem(backupKey, JSON.stringify({ draft: docRef.current, meta: meta.current, at: new Date().toISOString() })); } catch { /* storage off */ } };
  const dropBackup = () => { try { localStorage.removeItem(backupKey); } catch { /* storage off */ } };
  const [backup, setBackup] = useState<{ draft: MapBody; meta: Meta; at: string } | null>(() => {
    try {
      const raw = localStorage.getItem(`pmap-unsaved:${initial.id}`), b = raw ? JSON.parse(raw) : null;
      if (!b?.draft?.steps) return null;
      if (JSON.stringify(b.draft) === JSON.stringify(initial.draft) && !Object.keys(b.meta ?? {}).length) { localStorage.removeItem(`pmap-unsaved:${initial.id}`); return null; }
      return { draft: b.draft, meta: b.meta ?? {}, at: b.at };
    } catch { return null; }
  });
  useEffect(() => {
    const warn = (e: BeforeUnloadEvent) => { if (dirty.current || inflight.current) { keepBackup(); e.preventDefault(); } };
    window.addEventListener("beforeunload", warn);
    return () => { window.removeEventListener("beforeunload", warn); window.clearTimeout(timer.current); if (dirty.current) { keepBackup(); void doSave(); } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ----- edits -----
  const commit = (fn: (m: MapBody) => void, opts: { fix?: boolean } = {}) => {
    if (locked()) return false;
    const before = JSON.stringify(docRef.current), prev = JSON.parse(before), next: MapBody = JSON.parse(before);
    fn(next);
    if (opts.fix !== false) fixChoices(prev, next);
    if (JSON.stringify(next) === before) return false;
    undoS.current.push(before); if (undoS.current.length > 150) undoS.current.shift(); redoS.current = [];
    docRef.current = next; markDirty(); bump(); return true;
  };
  const editText = (fn: (m: MapBody) => void) => {
    if (locked()) return;
    const t = typing.current, now = JSON.stringify(docRef.current);
    if (!t || !t.pushed) { undoS.current.push(t?.snap ?? now); redoS.current = []; if (t) t.pushed = true; }
    const next: MapBody = JSON.parse(now); fn(next); docRef.current = next; markDirty(); bump();
  };
  const editMeta = (patch: Meta) => {
    if (locked()) return;
    if (patch.docNo !== undefined) setMetaErr(null);
    setD((x) => ({ ...x, ...patch, ...(patch.area !== undefined ? { areaName: AREAS.find((a) => a.id === patch.area)?.name ?? "" } : {}) }));
    meta.current = { ...meta.current, ...patch }; markDirty();
  };
  const afterSwap = () => {
    const B = byId(docRef.current);
    if (sel && !B[sel]) setSel(null);
    if (scen && !docRef.current.scenarios.some((s) => s.id === scen)) setScen(null);
    setWalk(null); markDirty(); bump();
  };
  const undo = () => { if (!undoS.current.length || locked()) return; redoS.current.push(JSON.stringify(docRef.current)); docRef.current = JSON.parse(undoS.current.pop()!); afterSwap(); };
  const redo = () => { if (!redoS.current.length || locked()) return; undoS.current.push(JSON.stringify(docRef.current)); docRef.current = JSON.parse(redoS.current.pop()!); afterSwap(); };
  /** Take what the server has (after a release, a confirmed chat change, or "load their version"). */
  // undo: "push" puts what you had on the undo stack (a chat change you can undo); "clear" starts over (a release, or
  // loading someone else's version).
  const adopt = (x: ProcessDetail, undo: "push" | "clear") => {
    if (undo === "push" && JSON.stringify(x.draft) !== JSON.stringify(docRef.current)) { undoS.current.push(JSON.stringify(docRef.current)); redoS.current = []; }
    else if (undo === "clear") { undoS.current = []; redoS.current = []; }
    docRef.current = clone(x.draft); ver.current = x.version; savedDocNo.current = x.docNo; dirty.current = false; meta.current = {};
    setD(x); setSave({ state: "saved" }); dropBackup(); bump();
  };

  // ----- Courage Chat knows which map is open; a change it proposes lands on the saved draft, then shows here -----
  const chatHooks = useRef({ flush, changed: async () => {} });
  chatHooks.current = {
    flush,
    changed: async () => { try { const x = await processes.get(d.id); adopt(x, "push"); setCompare(true); say("Courage Chat's change is in the draft.", { label: "Undo", fn: undo }); } catch { /* shown on the next load */ } },
  };
  useEffect(() => {
    setPageChat({ context: `process:${d.id}`, placeholder: ro ? "Ask about this process" : "Ask about this map, or tell Courage Chat what to change",
      prompts: ro ? CHAT_PROMPTS.slice(2) : CHAT_PROMPTS, beforeConfirm: () => chatHooks.current.flush(), onChanged: () => void chatHooks.current.changed() });
    return () => setPageChat(null);
  }, [d.id, ro]);

  // ----- what's on screen -----
  const starter = useStartFromMap();
  const [sel, setSel] = useState<string | null>(null);
  const [scen, setScen] = useState<string | null>(null);
  const [view, setView] = useState<View>("map");
  const [compare, setCompare] = useState(true);
  const [zoom, setZoom] = useState(() => (window.innerWidth < 700 ? 0.8 : 1));
  const [walk, setWalk] = useState<{ sc: string; i: number } | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [confirmRelease, setConfirmRelease] = useState(false);
  const [rename, setRename] = useState<{ id: string; value: string; all?: boolean } | null>(null);
  const [toast, setToastState] = useState<Toast>(null);
  const [sopWhich, setSopWhich] = useState<"draft" | "released">("draft");
  const [runPanel, setRunPanel] = useState<RunPanelState>(null);
  const [runSteps, setRunSteps] = useState<string[] | null>(null);
  const [runsKey, setRunsKey] = useState(0);
  const [laneRename, setLaneRename] = useState<{ id: string; value: string } | null>(null);
  // The details panel can be hidden on a wide screen, to give the map the room. Remembered in this browser.
  const [panelHidden, setPanelHiddenState] = useState(() => { try { return localStorage.getItem("pmap-panel") === "hidden"; } catch { return false; } });
  const setPanelHidden = (v: boolean) => { setPanelHiddenState(v); try { localStorage.setItem("pmap-panel", v ? "hidden" : "shown"); } catch { /* storage off */ } };
  const toastT = useRef<number | undefined>(undefined);
  const say = (msg: string, action?: { label: string; fn: () => void }) => { setToastState({ msg, action }); window.clearTimeout(toastT.current); toastT.current = window.setTimeout(() => setToastState(null), 5000); };
  const scrollRef = useRef<HTMLDivElement>(null);
  // On a phone the map fills the screen from where it starts (under the title and any banner) down to the details
  // sheet's handle: CSS gets where it starts as --pmap-stage-top.
  const stageRef = useRef<HTMLElement>(null);
  const placeStage = useCallback(() => {
    const el = stageRef.current; if (!el) return;
    const top = `${Math.round(el.getBoundingClientRect().top + window.scrollY)}px`;
    if (el.style.getPropertyValue("--pmap-stage-top") !== top) el.style.setProperty("--pmap-stage-top", top);
  }, []);
  useLayoutEffect(placeStage);
  useEffect(() => { window.addEventListener("resize", placeStage); return () => window.removeEventListener("resize", placeStage); }, [placeStage]);
  const railWidth = narrow() ? 96 : 128;

  const L = useMemo(() => layout(doc), [doc]);
  const diff = useMemo(() => diffMaps(draftDoc, d.released), [draftDoc, d.released]);
  const problems = useMemo(() => problemsOf(draftDoc), [draftDoc]);
  const B = byId(doc);
  const draftRev = nextRev(d.rev);
  const isDraft = !d.released || diff.count > 0;
  const ready = isDraft && !problems.length;
  const showDiff = compare && !!d.released && !showRel ? diff : null;
  const laneName = (id: string) => doc.lanes.find((l) => l.id === id)?.name ?? "No lane";
  const order = (id: string) => L.ordered.indexOf(id) + 1;
  const commentsBy = useMemo(() => { const o: Record<string, number> = {}; for (const c of d.comments) o[c.stepId] = (o[c.stepId] ?? 0) + 1; return o; }, [d.comments]);
  const active: Active = useMemo(() => {
    const id = walk?.sc ?? scen; const sc = id ? doc.scenarios.find((s) => s.id === id) : null;
    // With no scenario picked, an open run lights up the steps it went through.
    const list = sc ? pathOf(doc, sc) : runPanel?.mode === "view" && runSteps?.length ? runSteps.filter((x) => B[x]) : null;
    if (!list) return null;
    const edges = new Set<string>();
    for (let i = 0; i < list.length - 1; i++) edges.add(`${list[i]}>${list[i + 1]}`);
    return { nodes: new Set(list), edges, list };
  }, [doc, walk?.sc, scen, runPanel, runSteps]); // eslint-disable-line react-hooks/exhaustive-deps
  const plan = useMemo(() => agentPlan(d.released), [d.released]);
  const openRun = () => { setSel(null); setScen(null); setWalk(null); setRename(null); setRunPanel({ mode: "new" }); setPanelHidden(false); if (narrow()) setSheetOpen(true); };
  const openRunView = (number: string, run?: Run | null) => { setSel(null); setScen(null); setWalk(null); setRunPanel({ mode: "view", number, run }); setPanelHidden(false); if (narrow()) setSheetOpen(true); };

  // ----- moving around the map -----
  const ensureVisible = (id: string) => {
    const el = scrollRef.current, q = L.pos[id], s = B[id];
    if (view !== "map" || !el || !q || !s) return;
    const { w, h } = sz(s), z = zoom;
    const left = railWidth + (q.x - w / 2) * z - 24, right = railWidth + (q.x + w / 2) * z + 24, top = (q.y - h / 2) * z - 24, bottom = (q.y + h / 2) * z + 24;
    let x = el.scrollLeft, y = el.scrollTop;
    const pad = walk ? 190 : 0;
    if (left < el.scrollLeft + railWidth) x = left - railWidth; else if (right > el.scrollLeft + el.clientWidth) x = right - el.clientWidth;
    if (top < el.scrollTop) y = top; else if (bottom > el.scrollTop + el.clientHeight - pad) y = bottom - el.clientHeight + pad;
    el.scrollTo({ left: Math.max(0, x), top: Math.max(0, y), behavior: reduced() ? "auto" : "smooth" });
  };
  // A map just made from the blank template opens with its first step's name ready to type over.
  const loc = useLocation(), nav = useNavigate();
  useEffect(() => {
    if (!(loc.state as { fresh?: boolean } | null)?.fresh) return;
    nav(loc.pathname, { replace: true, state: null });
    const first = docRef.current.steps.find((x) => x.id === "step-1" && x.name === "First step");
    if (first && !ro) { setSel(first.id); setRename({ id: first.id, value: first.name, all: true }); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const pendingVisible = useRef<string | null>(null);
  useEffect(() => { if (pendingVisible.current) { ensureVisible(pendingVisible.current); pendingVisible.current = null; } });
  const select = (id: string | null, opts: { scroll?: boolean; sheet?: boolean } = {}) => {
    setSel(id); setConfirmRelease(false);
    if (id && narrow() && opts.sheet !== false) setSheetOpen(true);
    if (id && opts.scroll !== false) pendingVisible.current = id;
  };
  const zoomTo = (zRaw: number, at?: { clientX: number; clientY: number }) => {
    const z = Math.max(0.2, Math.min(1.6, Math.round(zRaw * 100) / 100)), el = scrollRef.current;
    if (!el) { setZoom(z); return; }
    const r = el.getBoundingClientRect(), cx = at ? at.clientX - r.left : el.clientWidth / 2, cy = at ? at.clientY - r.top : el.clientHeight / 2;
    const bx = (el.scrollLeft + cx - railWidth) / zoom, by = (el.scrollTop + cy) / zoom;
    setZoom(z);
    requestAnimationFrame(() => { el.scrollLeft = bx * z + railWidth - cx; el.scrollTop = by * z - cy; });
  };
  /** The whole map in view, as large as it fits (never above 100%). */
  const fit = () => {
    const el = scrollRef.current; if (!el) return;
    zoomTo(Math.min(1, (el.clientWidth - railWidth - 8) / L.W, (el.clientHeight - 8) / L.H));
    requestAnimationFrame(() => { el.scrollLeft = 0; el.scrollTop = 0; });
  };

  // ----- map edits -----
  const uid = (base: string) => slugId(base, new Set(docRef.current.steps.map((s) => s.id)));
  /** The next step after this one, in its lane or the one given; from a decision, a new branch. */
  const addAfter = (id: string, laneId?: string) => {
    const s = B[id]; if (!s || ro) return;
    if (s.type === "end") return say("The end step has no next step.");
    if (s.type === "decision") return addBranch(id, laneId);
    const nid = uid("step"), lane = laneId && doc.lanes.some((l) => l.id === laneId) ? laneId : s.lane;
    commit((m) => {
      const o = m.flows.filter((f) => f.from === id);
      m.steps.push(blankStep(nid, "New step", "task", lane, lane === s.lane ? s.owner : ""));
      if (o.length) { const t = o[0]; m.flows = m.flows.filter((f) => f !== t); m.flows.push({ from: id, to: nid, label: "" }, { from: nid, to: t.to, label: "" }); }
      else m.flows.push({ from: id, to: nid, label: "" });
    });
    select(nid, { sheet: false }); setRename({ id: nid, value: "New step", all: true });
  };
  const laneBelow = (laneId: string) => { const i = doc.lanes.findIndex((l) => l.id === laneId); return doc.lanes[Math.min(i + 1, doc.lanes.length - 1)].id; };
  const addDecision = (id: string) => {
    const s = B[id]; if (!s || ro) return;
    if (s.type === "decision") return addBranch(id);
    if (s.type === "end") return say("Add a decision before the end step instead.");
    const did = uid("decision"), nid = slugId("step", new Set([...docRef.current.steps.map((x) => x.id), did])), below = laneBelow(s.lane);
    commit((m) => {
      const o = m.flows.filter((f) => f.from === id);
      m.steps.push(blankStep(did, "New decision?", "decision", s.lane, s.owner), blankStep(nid, "New step", "task", below));
      m.flows = m.flows.filter((f) => f.from !== id);
      m.flows.push({ from: id, to: did, label: "" });
      if (o.length) m.flows.push({ from: did, to: o[0].to, label: "Yes" }, { from: did, to: nid, label: "No" }, { from: nid, to: o[0].to, label: "" });
      else m.flows.push({ from: did, to: nid, label: "Yes" });
    });
    select(did, { sheet: false }); setRename({ id: did, value: "New decision?", all: true });
  };
  const addBranch = (did: string, laneId?: string) => {
    const s = B[did]; if (!s || ro) return;
    const nid = uid("step"), o = outs(doc, did), lane = laneId && doc.lanes.some((l) => l.id === laneId) ? laneId : laneBelow(s.lane);
    commit((m) => {
      m.steps.push(blankStep(nid, "New step", "task", lane));
      m.flows.push({ from: did, to: nid, label: nextLabel(o) });
      if (o.length) m.flows.push({ from: nid, to: o[0].to, label: "" });
    });
    select(nid, { sheet: false }); setRename({ id: nid, value: "New step", all: true });
  };
  const removeStep = (id: string) => {
    const s = B[id]; if (!s || ro) return;
    if (s.type === "start") return say("Every map needs its start step.");
    if (s.type === "end" && docRef.current.steps.filter((x) => x.type === "end").length < 2) return say("Every map needs an end step. Rename it instead, or connect another step to it.");
    commit((m) => { dropStep(m, id); });
    setSel(null); say(`Deleted ${s.name}.`, { label: "Undo", fn: undo });
  };
  const moveLane = (id: string, laneId: string) => {
    const s = B[id], lane = doc.lanes.find((l) => l.id === laneId);
    if (!s || !lane || s.lane === laneId) return;
    if (commit((m) => { const x = m.steps.find((y) => y.id === id); if (x) x.lane = laneId; })) { say(`${s.name} moved to ${lane.name}.`, { label: "Undo", fn: undo }); pendingVisible.current = id; }
  };
  const moveLaneBy = (id: string, dir: number) => { const s = B[id]; if (!s) return; const i = doc.lanes.findIndex((l) => l.id === s.lane) + dir; if (i >= 0 && i < doc.lanes.length) moveLane(id, doc.lanes[i].id); };
  const connect = (from: string, to: string) => {
    const s = B[from]; if (!s || !B[to] || from === to || ro) return;
    if (s.type === "end") return say("The end step has no next step.");
    if (doc.flows.some((f) => f.from === from && f.to === to)) return say("These steps are already connected.");
    const dec = s.type === "decision";
    commit((m) => {
      if (dec) m.flows.push({ from, to, label: nextLabel(m.flows.filter((f) => f.from === from)) });
      else { m.flows = m.flows.filter((f) => f.from !== from); m.flows.push({ from, to, label: "" }); }
    });
    say(dec ? `Added a branch from ${s.name} to ${B[to].name}.` : `${s.name} now goes to ${B[to].name}.`, { label: "Undo", fn: undo });
  };
  const removeFlow = (from: string, to: string) => {
    if (outs(doc, from).length <= 1) return say("A step needs at least one next step. Connect another step first.");
    commit((m) => { m.flows = m.flows.filter((f) => !(f.from === from && f.to === to)); });
  };
  const startRename = (id: string, initialText?: string) => {
    if (ro || !B[id]) return;
    if (view === "outline") { document.getElementById(`pmap-ol-${id}`)?.focus(); return; }
    if (view !== "map") setView("map");
    setRename({ id, value: initialText ?? B[id].name, all: initialText === undefined }); pendingVisible.current = id;
  };
  /** `byKey`: ended with Enter or Esc, so the keyboard goes back to the map. A click elsewhere (into the details panel,
   *  say) leaves the focus where the person clicked. */
  const endRename = (keep: boolean, byKey = false) => {
    const r = rename; if (!r) return;
    setRename(null);
    const v = r.value.trim();
    if (keep && v && B[r.id] && v !== B[r.id].name) commit((m) => { const x = m.steps.find((y) => y.id === r.id); if (x) x.name = v.slice(0, 120); }, { fix: false });
    if (byKey) scrollRef.current?.focus({ preventScroll: true });
  };

  // ----- lanes on the canvas -----
  const addLane = () => {
    if (ro) return;
    const id = slugId("lane", new Set(docRef.current.lanes.map((l) => l.id))), name = `Lane ${docRef.current.lanes.length + 1}`;
    if (commit((m) => { m.lanes.push({ id, name }); }, { fix: false })) {
      setLaneRename({ id, value: name });
      requestAnimationFrame(() => { const el = scrollRef.current; if (el) el.scrollTo({ top: el.scrollHeight, behavior: reduced() ? "auto" : "smooth" }); });
    }
  };
  /** Renaming a lane in the rail: `save` takes the typed name (a blank one keeps the old name). */
  const onLaneRename = (r: { id: string; value: string } | null, save?: boolean, byKey = false) => {
    if (!save || !r) { setLaneRename(r && !save ? r : null); if (!r && byKey) scrollRef.current?.focus({ preventScroll: true }); return; }
    setLaneRename(null);
    const v = r.value.trim().slice(0, 60);
    if (v) commit((m) => { const l = m.lanes.find((x) => x.id === r.id); if (l) l.name = v; }, { fix: false });
    if (byKey) scrollRef.current?.focus({ preventScroll: true });
  };

  // ----- scenarios -----
  const addScenario = () => {
    const id = slugId("scenario", new Set(doc.scenarios.map((s) => s.id)));
    if (commit((m) => { m.scenarios.push({ id, name: `Scenario ${m.scenarios.length + 1}`, choices: {}, condition: "" }); })) {
      setScen(id); setSel(null); setPanelHidden(false); if (narrow()) setSheetOpen(true);
      say("New scenario. Choose its branch at each decision.");
    }
  };
  const setChoice = (scId: string, dec: string, to: string) => commit((m) => { const sc = m.scenarios.find((s) => s.id === scId); if (sc) sc.choices = { ...sc.choices, [dec]: to }; });
  const removeScenario = (scId: string) => {
    const sc = doc.scenarios.find((s) => s.id === scId); if (!sc) return;
    commit((m) => { m.scenarios = m.scenarios.filter((s) => s.id !== scId); }, { fix: false });
    setScen(null); say(`Removed ${sc.name}.`, { label: "Undo", fn: undo });
  };
  const release = async (note: string) => {
    if (busy) return;
    setBusy("release");
    try {
      await flush();
      const x = await processes.release(d.id, ver.current, note || undefined);
      adopt(x, "clear"); setConfirmRelease(false); setSopWhich("released");
      say(`Rev ${x.rev} released and filed as ${x.docRecord?.number ?? "a controlled document"}. People and agents follow it from now on.`, { label: "Open the SOP", fn: () => setView("sop") });
    } catch (e) { await changedUnderneath(e); }
    finally { setBusy(null); }
  };
  /** Someone changed the map after you looked at it: show the latest, so you release what you've seen. */
  const changedUnderneath = async (e: unknown) => {
    if (e instanceof ApiError && e.status === 409 && e.body?.code === "stale" && !dirty.current) {
      try { const x = await processes.get(d.id); adopt(x, "push"); setCompare(true); say(`${e.message} The map now shows the latest.`); return; } catch { /* fall through */ }
    }
    say(e instanceof Error ? e.message : String(e));
  };

  // ----- walk-through -----
  const startWalk = (scId: string) => { setWalk({ sc: scId, i: 0 }); setScen(scId); setSel(null); setView("map"); setSheetOpen(false); const sc = doc.scenarios.find((s) => s.id === scId); if (sc) pendingVisible.current = pathOf(doc, sc)[0]; };
  const walkStep = (dx: number) => {
    if (!walk || !active) return;
    const n = walk.i + dx;
    if (n >= active.list.length) { setWalk(null); return; }
    const i = Math.max(0, n); setWalk({ ...walk, i }); pendingVisible.current = active.list[i];
  };

  // ----- keyboard -----
  const keys = useRef<(e: KeyboardEvent) => void>(() => {});
  keys.current = (e: KeyboardEvent) => {
    const t = e.target as HTMLElement, typingIn = t.matches?.("input,textarea,select,[contenteditable='true']");
    const mod = e.metaKey || e.ctrlKey, k = e.key;
    if (mod && (k === "z" || k === "Z")) { if (typingIn) return; e.preventDefault(); if (e.shiftKey) redo(); else undo(); return; }
    if (mod && (k === "y" || k === "Y")) { if (typingIn) return; e.preventDefault(); redo(); return; }
    if (typingIn || mod) return;
    const onCanvas = t === document.body || t === scrollRef.current; // not a button on the map (+ Add lane, the toolbar)
    if (walk) {
      if (!onCanvas && !t.closest?.(".pmap-walk")) return;
      if (k === "ArrowRight") { e.preventDefault(); walkStep(1); } else if (k === "ArrowLeft") { e.preventDefault(); walkStep(-1); } else if (k === "Escape") setWalk(null);
      return;
    }
    if (view !== "map" || !onCanvas) return;
    const id = sel;
    if (k === "Escape") { if (id) setSel(null); return; }
    if (!id) { if (["Enter", "ArrowRight", "ArrowDown"].includes(k)) { const s = startId(doc); if (s) { e.preventDefault(); select(s, { sheet: false }); } } return; }
    if (!B[id] || !L.pos[id]) { setSel(null); return; } // the step is gone (undo, a chat change, someone else's save)
    const go = (to: string | undefined | null) => { if (to && B[to]) select(to, { sheet: false }); };
    switch (k) {
      case "ArrowRight": e.preventDefault(); go(outs(doc, id)[0]?.to); return;
      case "ArrowLeft": e.preventDefault(); go(ins(doc, id)[0]?.from); return;
      case "ArrowUp": case "ArrowDown": {
        e.preventDefault();
        if (e.altKey && !ro) { moveLaneBy(id, k === "ArrowUp" ? -1 : 1); return; }
        const q = L.pos[id], want = q.li + (k === "ArrowUp" ? -1 : 1);
        let best: string | null = null, bd = 1e9;
        for (const o in L.pos) { const p = L.pos[o]; if (p.li !== want) continue; const dd = Math.abs(p.r - q.r); if (dd < bd) { bd = dd; best = o; } }
        go(best); return;
      }
    }
    if (ro) return;
    switch (k) {
      case "Enter": e.preventDefault(); addAfter(id); break;
      case "Tab": e.preventDefault(); addDecision(id); break;
      case "F2": e.preventDefault(); startRename(id); break;
      case "Delete": case "Backspace": e.preventDefault(); removeStep(id); break;
      default: if (k.length === 1 && !e.altKey && /\S/.test(k)) { e.preventDefault(); startRename(id, k); }
    }
  };
  useEffect(() => { const h = (e: KeyboardEvent) => keys.current(e); document.addEventListener("keydown", h); return () => document.removeEventListener("keydown", h); }, []);

  const editCtx = useMemo(() => ({ ro: ro || !!busy, begin: () => { typing.current = { snap: JSON.stringify(docRef.current), pushed: false }; }, end: () => { typing.current = null; } }), [ro, busy]);
  const ctx: PanelCtx = { d, doc, B, L, diff, ro, draftRev, isDraft: isDraft && !showRel, problems, ready, busy, laneName, order, commit, editText, editMeta, select, setScen, setSel,
    addAfter, addDecision, addBranch, removeStep, removeFlow, connect, moveLane, startRename, release, startWalk, setChoice, removeScenario, confirmRelease, setConfirmRelease,
    metaErr, say, setD, closeSheet: () => setSheetOpen(false) };

  const saveLabel = ro ? (access?.viewAs?.readOnly ? "Viewing as someone else: read only" : "Read only: you can comment") : save.state === "saving" ? "Saving…" : save.state === "dirty" ? "Unsaved changes" : save.state === "saved" ? "Saved" : save.state === "error" ? "Not saved" : "Not saved: someone else saved";
  const sheetSum = sel && B[sel] ? `${B[sel].name} · ${laneName(B[sel].lane)}`
    : scen ? doc.scenarios.find((s) => s.id === scen)?.name ?? ""
      : runPanel ? (runPanel.mode === "new" ? "Run the agent" : `Agent run ${runPanel.number}`)
      : isDraft ? `Rev ${draftRev} draft · ${problems.length ? `${problems.length} to fix` : "ready to release"}` : `Rev ${d.rev} in effect`;
  const walkSc = walk ? doc.scenarios.find((s) => s.id === walk.sc) : null;
  const walkStepObj = walk && active ? B[active.list[Math.min(walk.i, active.list.length - 1)]] : null;

  return (
    <EditCtx.Provider value={editCtx}>
      <CommandBar status={<span className={`pmap-save ${save.state}`}>{saveLabel}</span>}>
        <Link className="cmd" to={d.area ? `/processes?area=${d.area}` : "/processes"}>All process maps</Link>
        {!ro && <><button className="cmd" onClick={undo} disabled={!undoS.current.length} title="Undo (Ctrl+Z)">Undo</button><button className="cmd" onClick={redo} disabled={!redoS.current.length} title="Redo (Ctrl+Shift+Z)">Redo</button></>}
        <span className="pmap-seg" role="group" aria-label="View">
          {(["map", "outline", "sop", "runs"] as View[]).map((v) => <button key={v} aria-pressed={view === v} onClick={() => { setView(v); setRename(null); setWalk(null); }}>{v === "map" ? "Map" : v === "outline" ? "Outline" : v === "sop" ? "SOP" : "Runs"}</button>)}
        </span>
        {plan.steps.length > 0 && <button className="cmd" aria-pressed={runPanel?.mode === "new"} onClick={openRun} title={`Hand a customer PO to the agent: it does ${plan.steps.map((x) => x.name).join(", ")}`}>Run agent</button>}
        <button className="cmd" onClick={openChat} title="Ask about this map, or ask for a change to it">Ask Courage Chat</button>
        <button className="cmd" disabled={starter.busy} onClick={() => starter.start(d.id, "issue", sel ?? undefined)} title={sel ? "Raise an issue at the selected step" : "Raise an issue in this process"}>Raise an issue</button>
        <button className="cmd" disabled={starter.busy} onClick={() => starter.start(d.id, "improve", sel ?? undefined)} title={sel ? "Improve the selected step" : "Improve this process"}>Improve this process</button>
        {!noEdit && (isDraft
          ? <button className="cmd primary" disabled={showRel} onClick={() => { setSel(null); setScen(null); setRunPanel(null); setPanelHidden(false); setConfirmRelease(ready); if (narrow()) setSheetOpen(true); if (!ready) say("Fix the map before release."); }}>Release Rev {draftRev}</button>
          : <span className="pmap-live">Rev {d.rev} in effect</span>)}
      </CommandBar>

      <div className={`pmap ${sheetOpen ? "sheet-open" : ""}`}>
        <header className="pmap-head">
          <div className="pmap-titleline">
            <h1>{d.title}</h1>
            <span className="mono pmap-docno">{d.docNo}</span>
            {d.released && isDraft ? (
              // Both exist: the revision in effect (what people and agents follow) and the draft being edited.
              <span className="pmap-seg pmap-revseg" role="group" aria-label="Which revision">
                <button aria-pressed={showRel} onClick={() => { setShowRel(true); setSel(null); setWalk(null); setRename(null); setLaneRename(null); }}>Rev {d.rev} in effect</button>
                <button aria-pressed={!showRel} onClick={() => { setShowRel(false); setSel(null); setWalk(null); }}>Rev {draftRev} draft</button>
              </span>
            ) : <span className={`pmap-rev ${isDraft && !showRel ? "draft" : "rel"}`}>{isDraft && !showRel ? `Rev ${draftRev} draft` : `Rev ${d.rev} in effect`}</span>}
            {d.released && isDraft && !showRel && <button className="pmap-compare" aria-pressed={compare} onClick={() => setCompare(!compare)} title={`Mark what changed since Rev ${d.rev}`}>Mark changes</button>}
            {d.areaName && <Link className="pmap-area" to={`/a/${d.area}`}>{d.areaName}</Link>}
          </div>
        </header>

        {save.state === "stale" && (
          <div className="notify warn pmap-stale" role="alert">
            <span>{save.msg} Your changes aren't saved.</span>
            <span className="btn-row">
              {save.current && <button className="btn" onClick={() => { adopt(save.current!, "clear"); say("Loaded the latest version."); }}>Load their version</button>}
              <button className="btn primary" onClick={() => { if (save.current) ver.current = save.current.version; setSave({ state: "dirty" }); dirty.current = true; void doSave(); }}>Keep mine</button>
            </span>
          </div>
        )}
        {backup && !noEdit && (
          <div className="notify warn pmap-stale" role="alert">
            <span>Changes you made to this map on {new Date(backup.at).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })} weren't saved. Restoring puts them back over the saved draft.</span>
            <span className="btn-row">
              <button className="btn primary" onClick={() => {
                undoS.current.push(JSON.stringify(docRef.current)); redoS.current = [];
                docRef.current = clone(backup.draft); meta.current = { ...backup.meta };
                if (Object.keys(backup.meta).length) setD((x) => ({ ...x, ...backup.meta }));
                setBackup(null); setShowRel(false); markDirty(); bump(); say("Your changes are back in the draft.", { label: "Undo", fn: undo });
              }}>Restore them</button>
              <button className="btn" onClick={() => { dropBackup(); setBackup(null); }}>Discard</button>
            </span>
          </div>
        )}
        {save.state === "error" && <div className="notify warn pmap-stale" role="alert"><span>Your last change wasn't saved: {save.msg}</span><button className="btn" onClick={() => { setSave({ state: "dirty" }); void doSave(); }}>Try again</button></div>}

        <nav className="pmap-scen-bar" aria-label="Scenarios">
          <span className="pmap-sb-label">Scenarios</span>
          <button className="pmap-chip" aria-pressed={!scen} onClick={() => { setScen(null); setSel(null); setWalk(null); }}>All steps</button>
          {doc.scenarios.map((sc) => (
            <button key={sc.id} className="pmap-chip" aria-pressed={scen === sc.id} onClick={() => { const nx = scen === sc.id ? null : sc.id; setScen(nx); setSel(null); setWalk(null); if (narrow() && nx) setSheetOpen(true); }}>{sc.name}</button>))}
          {scen && <button className="pmap-chip pmap-chip-add" onClick={() => startWalk(scen)}>Walk through</button>}
          {!ro && <button className="pmap-chip pmap-chip-add" onClick={addScenario}>New scenario</button>}
        </nav>

        {showRel && d.released && (
          <div className="pmap-banner ready" role="status">
            <span className="pmap-banner-msg"><b>Rev {d.rev} is in effect</b>, released {fmtDate(d.releasedAt)}{d.releasedByName ? ` by ${d.releasedByName}` : ""}. People and agents follow it.{isDraft ? ` A Rev ${draftRev} draft is in progress.` : ""}</span>
            {isDraft && <button className="btn" onClick={() => { setShowRel(false); setSel(null); }}>See the Rev {draftRev} draft</button>}
          </div>
        )}
        {!showRel && isDraft && d.released && diff.count > 0 && (
          <div className={`pmap-banner ${problems.length ? "" : "ready"}`} role="status">
            <span className="pmap-banner-msg"><b>Draft Rev {draftRev}</b> · {bannerParts(diff)} since Rev {d.rev}. {problems.length ? "Fix the map before release." : "Ready to release."}
              {compare && diff.removed.length > 0 && <> Removed: <span className="removed">{diff.removed.map((s) => s.name).join(", ")}</span>.</>}</span>
            {!ro && (problems.length ? <button className="btn" onClick={() => { setSel(null); setScen(null); setRunPanel(null); setPanelHidden(false); if (narrow()) setSheetOpen(true); }}>See what to fix</button>
              : <button className="btn primary" onClick={() => { setSel(null); setScen(null); setRunPanel(null); setPanelHidden(false); setConfirmRelease(true); if (narrow()) setSheetOpen(true); }}>Release Rev {draftRev}</button>)}
          </div>
        )}
        {!d.released && (
          <div className="pmap-banner" role="status">
            <span className="pmap-banner-msg"><b>Not released yet.</b> {ro ? "People and agents follow this map once it's released." : "Map the steps, then release Rev A. Document control files it, and people and agents follow it from then on."}</span>
          </div>
        )}

        <div className={`pmap-work${panelHidden && view === "map" ? " panel-hidden" : ""}`}>
          <section className="pmap-stage" ref={stageRef} aria-label="Process map">
            <div className="pmap-canvas-wrap">
              {view === "map" && (
                <div className="pmap-scroll" ref={scrollRef} tabIndex={0} aria-label="Swimlane map. Arrow keys move between steps. The Outline view lists every step.">
                  <MapCanvas doc={doc} L={L} diff={showDiff} active={active} sel={sel} walkCur={walkStepObj?.id ?? null} zoom={zoom} ro={ro} comments={commentsBy} railWidth={railWidth}
                    rename={rename} laneRename={laneRename} panelHidden={panelHidden && view === "map" && !narrow()} scrollRef={scrollRef} onSelect={(id) => select(id, { scroll: false })} onMoveLane={moveLane} onConnect={connect}
                    onAddNext={(id, lane) => addAfter(id, lane)} onAddDecision={addDecision} onDelete={removeStep} onShowPanel={() => setPanelHidden(false)}
                    onStartRename={(id) => startRename(id)} onRenameChange={(value) => setRename((r) => (r ? { ...r, value } : r))} onRenameEnd={endRename}
                    onLaneRename={onLaneRename} onAddLane={addLane} onZoom={zoomTo} />
                </div>
              )}
              {view === "outline" && <Outline ctx={ctx} active={active} sel={sel} showDiff={showDiff} />}
              {view === "runs" && <RunList d={d} sel={runPanel?.mode === "view" ? runPanel.number : null} onOpen={(no) => openRunView(no)} onNew={openRun} reloadKey={runsKey} />}
              {view === "sop" && <div className="pmap-pane"><SopView d={{ ...d, draft: draftDoc }} which={d.released && (sopWhich === "released" || showRel) ? "released" : "draft"} onWhich={(w) => { setSopWhich(w); if (w === "draft") setShowRel(false); else if (noEdit) setShowRel(true); }} draftRev={draftRev} hasDraft={isDraft} /></div>}
              {view === "map" && (
                <div className="pmap-zoom" role="group" aria-label="Zoom">
                  <button onClick={() => zoomTo(zoom / 1.15)} aria-label="Zoom out">−</button>
                  <button className="zl" onClick={() => zoomTo(1)} aria-label="Reset zoom">{Math.round(zoom * 100)}%</button>
                  <button onClick={() => zoomTo(zoom * 1.15)} aria-label="Zoom in">+</button>
                  <span className="sep" /><button onClick={fit} title="Show the whole map">Fit</button>
                  {!narrow() && <><span className="sep" /><button className="pmap-panel-tog" onClick={() => setPanelHidden(!panelHidden)} aria-expanded={!panelHidden}
                    title={panelHidden ? "Show the details panel" : "Hide the details panel to give the map more room"}>{panelHidden ? "Show details" : "Hide details"}</button></>}
                </div>
              )}
              {view === "map" && !ro && !walk && <MapHint step={sel ? B[sel] ?? null : null} lastEnd={Object.values(B).filter((x) => x.type === "end").length < 2} />}
              {walk && walkSc && walkStepObj && active && (
                <div className="pmap-walk" role="dialog" aria-label={`Walk through ${walkSc.name}`}>
                  <div className="wk-top"><span>{walkSc.name} · step {walk.i + 1} of {active.list.length}</span><span>{wiNumber(d.docNo, order(walkStepObj.id))}</span></div>
                  <div className="pmap-prog" aria-hidden="true">{active.list.map((_, i) => <i key={i} className={i <= walk.i ? "on" : ""} />)}</div>
                  <h3>{walkStepObj.name}</h3>
                  <div className="meta">{laneName(walkStepObj.lane)}{doneBy(doc, walkStepObj) === "agent" ? " · done by an agent" : walkStepObj.owner && walkStepObj.owner !== laneName(walkStepObj.lane) ? ` · ${walkStepObj.owner}` : ""}{sysText(walkStepObj) ? ` · ${sysText(walkStepObj)}` : ""}</div>
                  <p>{walkStepObj.instruction || "No instruction written yet."}</p>
                  <OpenScreen s={walkStepObj} />
                  <div className="btn-row"><button className="btn" disabled={walk.i === 0} onClick={() => walkStep(-1)}>Back</button><button className="btn primary" onClick={() => walkStep(1)}>{walk.i === active.list.length - 1 ? "Finish" : "Next"}</button><button className="btn" onClick={() => setWalk(null)}>Exit</button></div>
                </div>
              )}
            </div>
          </section>

          <aside className={`pmap-panel ${sheetOpen ? "open" : ""}`} aria-label="Details">
            <button className="pmap-sheet-bar" aria-expanded={sheetOpen} onClick={() => setSheetOpen(!sheetOpen)}><span className="grip" /><span className="sum">{sheetSum}</span></button>
            <div className="pmap-panel-body">
              {sel && B[sel] ? <StepPanel key={sel} ctx={ctx} s={B[sel]} />
                : scen && doc.scenarios.some((s) => s.id === scen) ? <ScenarioPanel key={scen} ctx={ctx} sc={doc.scenarios.find((s) => s.id === scen)!} />
                  : runPanel?.mode === "new" ? <RunStart d={d} onRun={(r) => { openRunView(r.number, r); setRunsKey((k) => k + 1); }} onClose={() => setRunPanel(null)} />
                    : runPanel?.mode === "view" ? <RunView key={runPanel.number} number={runPanel.number} initial={runPanel.run} onClose={() => { setRunPanel(null); setRunSteps(null); }} onSteps={setRunSteps} onAgain={openRun} />
                      : <Overview ctx={ctx} />}
            </div>
          </aside>
        </div>
        {toast && <div className="pmap-toast" role="status" aria-live="polite"><span>{toast.msg}</span>{toast.action && <button onClick={() => { setToastState(null); toast.action!.fn(); }}>{toast.action.label}</button>}</div>}
      </div>
    </EditCtx.Provider>
  );
}

/** One line at the foot of the map: how to do the next thing, for what's selected. Keyboard only matters on a desktop. */
function MapHint({ step, lastEnd }: { step: Step | null; lastEnd: boolean }) {
  const parts: [string, string][] = !step ? [["Click", "a step to edit it"], ["Double-click", "a lane to rename it"], ["Drag", "the background to move around"]]
    : step.type === "decision" ? [["Enter", "adds a branch"], ["Type", "to rename"], ["Drag +", "onto a step to branch there"], ["Delete", "removes it"]]
      : step.type === "end" ? (lastEnd ? [["Type", "to rename"]] : [["Type", "to rename"], ["Delete", "removes it"]])
        : [["Enter", "next step"], ["Tab", "decision"], ["Type", "to rename"], ["Drag +", "to connect or into a lane"], ["Alt ↑↓", "change lane"]];
  return <div className="pmap-hint" aria-hidden="true">{parts.map(([k, t], i) => <span key={i}><b>{k}</b> {t}</span>)}</div>;
}

function bannerParts(df: ReturnType<typeof diffMaps>) {
  const parts: string[] = [], nc = Object.keys(df.changed).length, sc = df.scenarios;
  if (nc) parts.push(`${nc} step${nc > 1 ? "s" : ""} changed`);
  if (df.added.length) parts.push(`${df.added.length} added`);
  if (df.removed.length) parts.push(`${df.removed.length} removed`);
  if (df.lanes) parts.push("lanes updated");
  const ns = sc.added.length + sc.changed.length + sc.removed.length;
  if (ns) parts.push(`${ns} scenario${ns > 1 ? "s" : ""} updated`);
  return parts.join(", ");
}

// ---------------------------------------------------------------------------------------------------------------
// The inspector: the map's overview, a step, or a scenario
// ---------------------------------------------------------------------------------------------------------------
type PanelCtx = {
  d: ProcessDetail; doc: MapBody; B: Record<string, Step>; L: ReturnType<typeof layout>; diff: ReturnType<typeof diffMaps>; ro: boolean; draftRev: string; isDraft: boolean;
  problems: { stepId?: string; text: string }[]; ready: boolean; busy: string | null;
  laneName: (id: string) => string; order: (id: string) => number;
  commit: (fn: (m: MapBody) => void, o?: { fix?: boolean }) => boolean; editText: (fn: (m: MapBody) => void) => void; editMeta: (p: Meta) => void;
  select: (id: string | null, o?: { scroll?: boolean; sheet?: boolean }) => void; setScen: (id: string | null) => void; setSel: (id: string | null) => void;
  addAfter: (id: string) => void; addDecision: (id: string) => void; addBranch: (id: string) => void; removeStep: (id: string) => void; removeFlow: (a: string, b: string) => void;
  connect: (a: string, b: string) => void; moveLane: (id: string, lane: string) => void; startRename: (id: string) => void;
  release: (note: string) => Promise<void>; startWalk: (id: string) => void; setChoice: (sc: string, dec: string, to: string) => void; removeScenario: (id: string) => void;
  confirmRelease: boolean; setConfirmRelease: (v: boolean) => void; metaErr: string | null; say: (m: string) => void; closeSheet: () => void;
  setD: React.Dispatch<React.SetStateAction<ProcessDetail>>;
};

const Sec = ({ title, side, children }: { title: ReactNode; side?: ReactNode; children: ReactNode }) => (
  <div className="pmap-sec">{(title || side) && <div className="pmap-sec-h"><span className="pmap-sec-t">{title}</span>{side}</div>}{children}</div>
);

function Overview({ ctx }: { ctx: PanelCtx }) {
  const { d, doc, B, diff, ro, draftRev, isDraft, problems, ready } = ctx;
  const [note, setNote] = useState("");
  const viewingRel = doc === d.released; // the revision in effect is on screen, not the draft
  const items: [string, string, string][] = d.released && !viewingRel ? [...diff.added.map((id) => [id, "added", "New"] as [string, string, string]), ...Object.keys(diff.changed).map((id) => [id, "changed", "Changed"] as [string, string, string])] : [];
  const agentSteps = doc.steps.filter((s) => s.type !== "start" && s.type !== "end" && doneBy(doc, s) === "agent").length;
  return (
    <>
      <div className="pmap-p-head">
        <span className="pmap-eyebrow">Standard operating procedure</span>
        {ro ? <h2 className="pmap-h2">{d.title}</h2> : <input className="pmap-p-title" value={d.title} maxLength={120} aria-label="Process name" aria-invalid={!d.title.trim()} onChange={(e) => ctx.editMeta({ title: e.target.value })} />}
        {!ro && !d.title.trim() && <span className="pmap-field-err">Give the process a name. The rest of the map keeps saving.</span>}
        <dl className="pmap-kv">
          <dt>Document</dt><dd>{ro || d.rev ? <span className="mono">{d.docNo}</span> : <>
            <input className="pmap-inline mono" value={d.docNo} maxLength={40} aria-label="Document number" aria-invalid={!DOC_NO.test(d.docNo) || !!ctx.metaErr} onChange={(e) => ctx.editMeta({ docNo: e.target.value.toUpperCase() })} />
            {(ctx.metaErr || !DOC_NO.test(d.docNo)) && <span className="pmap-field-err">{ctx.metaErr ?? "Letters and numbers in parts, like SOP-SC-014. Saved when it's complete."}</span>}</>}</dd>
          <dt>Area</dt><dd>{ro ? d.areaName || "–" : <select className="pmap-inline" value={d.area ?? ""} aria-label="Area" onChange={(e) => ctx.editMeta({ area: e.target.value || null })}><option value="">No area</option>{AREAS.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}</select>}</dd>
          <dt>Process owner</dt><dd>{ro ? d.owner || "–" : <input className="pmap-inline" value={d.owner ?? ""} maxLength={80} aria-label="Process owner" placeholder="A role, e.g. Supply chain manager" onChange={(e) => ctx.editMeta({ owner: e.target.value })} />}</dd>
          <dt>In effect</dt><dd>{d.rev ? `Rev ${d.rev}, ${fmtDate(d.releasedAt)}` : "Not released"}</dd>
          <dt>Done by agents</dt><dd>{agentSteps ? `${agentSteps} step${agentSteps === 1 ? "" : "s"}` : "None"}</dd>
          {d.docRecord && <><dt>Document control</dt><dd><Link to={`/m/doc-control/${d.docRecord.number}`}>{d.docRecord.number}</Link></dd></>}
        </dl>
        {ro ? (d.summary && <p className="pmap-note">{d.summary}</p>) : <textarea className="pmap-summary" value={d.summary} maxLength={500} rows={2} placeholder="Purpose: what this process does and when it applies" aria-label="Purpose" onChange={(e) => ctx.editMeta({ summary: e.target.value })} />}
      </div>

      <Sec title={isDraft ? `Release Rev ${draftRev}` : `Rev ${d.rev} in effect`} side={isDraft ? <span className={`pmap-status ${ready ? "ok" : "warn"}`}>{ready ? "Ready" : `${problems.length} to fix`}</span> : undefined}>
        {items.length > 0 && <div>{items.map(([id, k, t]) => <div key={id} className="pmap-srow"><span className={`pmap-tagx ${k}`}>{t}</span><button className="pmap-linkish" onClick={() => ctx.select(id)}>{B[id]?.name}</button><span className="sub">{ctx.laneName(B[id]?.lane)}</span></div>)}
          {!viewingRel && diff.removed.map((s) => <div key={s.id} className="pmap-srow"><span className="pmap-tagx changed">Removed</span><span className="removed">{s.name}</span><span /></div>)}</div>}
        {!viewingRel && problems.length > 0 && <ul className="pmap-problems">{problems.map((p, i) => <li key={i}>{p.stepId ? <button className="pmap-linkish" onClick={() => ctx.select(p.stepId!)}>{p.text}</button> : p.text}</li>)}</ul>}
        {isDraft && !ro && (ctx.confirmRelease && ready ? (
          <div className="pmap-confirm">
            <b>Release Rev {draftRev}?</b>
            <span>{d.released ? summaryOf(diff, doc) : "First release."} Document control files it, and people and agents follow it from then on.</span>
            <label className="pmap-fld"><span>What changed and why (goes in the revision history)</span><textarea rows={2} maxLength={1000} value={note} onChange={(e) => setNote(e.target.value)} placeholder={d.released ? summaryOf(diff, doc) : "First release."} /></label>
            <div className="btn-row"><button className="btn primary" disabled={!!ctx.busy} onClick={() => void ctx.release(note.trim())}>{ctx.busy === "release" ? "Releasing…" : `Release Rev ${draftRev}`}</button><button className="btn" onClick={() => ctx.setConfirmRelease(false)}>Cancel</button></div>
          </div>
        ) : (
          <div><button className="btn primary" disabled={!ready || !!ctx.busy} onClick={() => ctx.setConfirmRelease(true)}>Release Rev {draftRev}</button>
            <p className="pmap-note">{ready ? "Releasing is the approval. People and agents follow the revision in effect, never the draft." : "Fix the map first."}</p></div>
        ))}
        {!isDraft && <p className="pmap-note">{doc === d.released && diff.count > 0 ? `This is Rev ${d.rev} as released. A Rev ${draftRev} draft is in progress.` : `No changes since Rev ${d.rev}.${ro ? "" : ` Edit the map to start Rev ${draftRev}.`}`}</p>}
      </Sec>

      <Sec title="Revision history">
        <ul className="pmap-hist">
          {isDraft && <li><span className="rv draft">{draftRev}</span><div><div>Draft in progress</div><div className="d">{d.released ? summaryOf(diff, doc) : "Not released yet"}</div></div></li>}
          {d.revisions.map((x) => <li key={x.rev}><span className="rv">{x.rev}</span><div><div>{x.note}</div><div className="d">{fmtDate(x.releasedAt)} · {x.releasedByName}</div></div></li>)}
        </ul>
      </Sec>

      <Sec title="Lanes" side={!ro && <button className="btn pmap-sm" onClick={() => ctx.commit((m) => { m.lanes.push({ id: slugId("lane", new Set(m.lanes.map((l) => l.id))), name: "New lane" }); }, { fix: false })}>Add lane</button>}>
        <div className="pmap-lanes-ed">{doc.lanes.map((l, i) => { const n = doc.steps.filter((s) => s.lane === l.id).length; return (
          <div key={l.id} className="lr">
            <TIn required value={l.name} maxLength={60} aria-label="Lane name" onText={(v) => ctx.editText((m) => { const x = m.lanes.find((y) => y.id === l.id); if (x) x.name = v; })} />
            <span className="btn-row">
              {(!ro || l.agent) && <button className="btn pmap-sm pmap-agent-tog" aria-pressed={!!l.agent} disabled={ro} title={l.agent ? "An AI agent does the steps in this lane" : "Make this an agent lane: an AI agent does its steps"}
                onClick={() => ctx.commit((m) => { const x = m.lanes.find((y) => y.id === l.id); if (!x) return; if (x.agent) delete x.agent; else x.agent = true; }, { fix: false })}>Agent</button>}
              {!ro && <>
                <button className="btn pmap-sm" disabled={i === 0} aria-label={`Move ${l.name} up`} title="Move up" onClick={() => ctx.commit((m) => { const k = m.lanes.findIndex((y) => y.id === l.id); if (k > 0) [m.lanes[k - 1], m.lanes[k]] = [m.lanes[k], m.lanes[k - 1]]; }, { fix: false })}>↑</button>
                <button className="btn pmap-sm" disabled={i === doc.lanes.length - 1} aria-label={`Move ${l.name} down`} title="Move down" onClick={() => ctx.commit((m) => { const k = m.lanes.findIndex((y) => y.id === l.id); if (k >= 0 && k < m.lanes.length - 1) [m.lanes[k + 1], m.lanes[k]] = [m.lanes[k], m.lanes[k + 1]]; }, { fix: false })}>↓</button>
                <button className="btn pmap-sm" disabled={n > 0 || doc.lanes.length < 2} title={n ? "Move its steps to another lane first" : undefined} onClick={() => ctx.commit((m) => { m.lanes = m.lanes.filter((y) => y.id !== l.id); }, { fix: false })}>Remove</button>
              </>}
            </span>
          </div>); })}</div>
        <p className="pmap-note">A lane is who does the work. In an agent lane, an AI agent does the steps through CourageMFG and Business Central, from the revision in effect.</p>
      </Sec>

      {!ro && (
        <Sec title="Keyboard">
          <div className="pmap-keys">
            <kbd>Enter</kbd><span>Add the next step</span><kbd>Tab</kbd><span>Add a decision or branch</span><kbd>← →</kbd><span>Follow the flow</span>
            <kbd>↑ ↓</kbd><span>Step in the lane above or below</span><kbd>Alt ↑ ↓</kbd><span>Move the step to another lane</span><kbd>Type</kbd><span>Rename the selected step</span>
            <kbd>Delete</kbd><span>Delete the step</span><kbd>Ctrl Z</kbd><span>Undo</span><kbd>Esc</kbd><span>Clear the selection</span>
          </div>
        </Sec>
      )}
    </>
  );
}

function DiffBox({ ctx, s }: { ctx: PanelCtx; s: Step }) {
  const { d, diff, draftRev } = ctx;
  if (!d.released || ctx.doc === d.released) return null;
  const r = byId(d.released)[s.id];
  if (diff.added.includes(s.id)) return <div className="pmap-diffbox added"><b>New in Rev {draftRev}</b></div>;
  if (diff.changed[s.id] && r) {
    const laneOf = (id: string, m: { lanes: Lane[] }) => { const l = m.lanes.find((x) => x.id === id); return l ? laneLabel(l) : "No lane"; };
    const fmt = (k: string, v: unknown, m: { lanes: Lane[] }) => (k === "lane" ? laneOf(String(v), m) : k === "system" ? SYSTEM_LABEL[v as SystemKind] ?? String(v) : String(v || "blank"));
    return (
      <div className="pmap-diffbox"><b>Changed since Rev {d.rev}</b>
        {diff.changed[s.id].map((k) => k === "next" ? <span key={k}>Next step changed</span> : k === "instruction" ? <span key={k}>Instruction rewritten</span> : k === "link" ? <span key={k}>Linked instruction changed</span>
          : <span key={k}>{FIELD_LABEL[k]}: <span className="from">{fmt(k, (r as any)[k], d.released!)}</span> → {fmt(k, (s as any)[k], ctx.doc)}</span>)}
      </div>
    );
  }
  return null;
}

function StepPanel({ ctx, s }: { ctx: PanelCtx; s: Step }) {
  const { d, doc, B, L, ro, diff, draftRev } = ctx;
  const [comment, setComment] = useState("");
  const [posting, setPosting] = useState(false);
  const set = (fn: (x: Step) => void) => ctx.editText((m) => { const x = m.steps.find((y) => y.id === s.id); if (x) fn(x); });
  const setNow = (fn: (x: Step) => void) => ctx.commit((m) => { const x = m.steps.find((y) => y.id === s.id); if (x) fn(x); }, { fix: false });
  const typeLabel = { task: "Step", decision: "Decision", start: "Start", end: "End" }[s.type];
  const changed = doc !== d.released && (diff.added.includes(s.id) || !!diff.changed[s.id] || !d.released);
  const o = outs(doc, s.id);
  const cm = d.comments.filter((c) => c.stepId === s.id);
  const agent = doneBy(doc, s) === "agent";
  const more = [s.owner && "owner", s.type !== "end" && s.kpi && "KPI", s.type !== "end" && s.control && "control", s.type !== "end" && s.link && "linked instruction"].filter(Boolean) as string[];
  const options = (exclude: (id: string) => boolean) => L.ordered.filter((id) => id !== s.id && B[id] && B[id].type !== "start" && !exclude(id));
  const post = async () => {
    const body = comment.trim(); if (!body) return;
    setPosting(true);
    try { const c = await processes.comment(d.id, s.id, body); ctx.setD((x) => ({ ...x, comments: [...x.comments, c] })); setComment(""); }
    catch (e) { ctx.say(e instanceof Error ? e.message : String(e)); } finally { setPosting(false); }
  };
  const del = async (id: string) => { try { await processes.deleteComment(id); ctx.setD((x) => ({ ...x, comments: x.comments.filter((c) => c.id !== id) })); } catch (e) { ctx.say(e instanceof Error ? e.message : String(e)); } };

  return (
    <>
      <div className="pmap-p-head">
        <div className="row"><span className="pmap-eyebrow">{typeLabel} {String(ctx.order(s.id)).padStart(2, "0")} · {ctx.laneName(s.lane)}</span><button className="btn pmap-sm" onClick={() => ctx.setSel(null)}>Close</button></div>
        {ro ? <h2 className="pmap-h2">{s.name}</h2> : <TIn required className="pmap-p-title" value={s.name} maxLength={120} aria-label="Step name" onText={(v) => set((x) => { x.name = v; })} />}
        {agent && s.type !== "start" && s.type !== "end" && <span className="pmap-agent-note">An agent does this step, from the revision in effect.</span>}
        <DiffBox ctx={ctx} s={s} />
        {(s.type === "task" || s.type === "decision") && <StepStarts mapId={d.id} stepId={s.id} />}
      </div>
      <div className="pmap-fields">
        <label className="pmap-fld full"><span>Lane: who does it</span>
          <select value={s.lane} disabled={ro} onChange={(e) => ctx.moveLane(s.id, e.target.value)}>{doc.lanes.map((l) => <option key={l.id} value={l.id}>{laneLabel(l)}</option>)}</select></label>
        {s.type !== "end" && <>
          <div className="pmap-fld full"><span>System: where it happens</span>
            <div className="pmap-sys-seg" role="group" aria-label="System">
              {(["courage", "bc", "manual"] as SystemKind[]).map((k) => <button key={k} type="button" disabled={ro} aria-pressed={s.system === k} onClick={() => setNow((x) => { x.system = k; })}><i className={`pmap-sw-${k}`} />{k === "courage" ? "courageMFG" : k === "bc" ? "BC page" : "Manual"}</button>)}
            </div></div>
          {(s.system !== "manual" || s.screen) && <label className="pmap-fld full"><span>{s.system === "bc" ? "BC page" : s.system === "courage" ? "courageMFG screen" : "Where it happens"}</span>
            <TIn value={s.screen} maxLength={80} list={s.system !== "manual" ? `pmap-screens-${s.system}` : undefined} placeholder={s.system === "bc" ? "e.g. Sales Order" : s.system === "courage" ? "e.g. Issues" : ""} onText={(v) => set((x) => { x.screen = v; })} />
            {s.system !== "manual" && <datalist id={`pmap-screens-${s.system}`}>{screenNames(s.system).map((n) => <option key={n} value={n} />)}</datalist>}
            {s.system !== "manual" && s.screen.trim() && !screenTarget(s) && <span className="pmap-note">{s.system === "bc" ? "Not a BC page CourageMFG knows, so it won't open from here. Pick one from the list to link it." : "Not a CourageMFG screen, so it won't open from here. Pick one from the list to link it."}</span>}
          </label>}
          <OpenScreen s={s} />
        </>}
      </div>

      {s.type !== "end" && (
        <Sec title="Instruction" side={<span className="pmap-wi-id">{wiNumber(d.docNo, ctx.order(s.id))} · {changed ? `Rev ${draftRev} draft` : `Rev ${d.rev}`}</span>}>
          <label className="pmap-fld"><span className="pmap-note">{agent ? "What the agent does, in plain words. Agents follow it from the revision in effect." : "What the person does, in plain words."} It rolls up into the SOP.</span>
            <TArea value={s.instruction} maxLength={2000} rows={4} aria-label="Instruction" onText={(v) => set((x) => { x.instruction = v; })} /></label>
        </Sec>
      )}

      <details className="pmap-more">
        <summary>More details{more.length ? <span className="sub"> · {more.join(", ")}</span> : null}</summary>
        <div className="pmap-fields">
          <label className="pmap-fld"><span>Owner role</span><TIn value={s.owner} maxLength={80} placeholder={agent ? "Who answers for the agent's work" : ""} onText={(v) => set((x) => { x.owner = v; })} /></label>
          {s.type !== "end" && <>
            <label className="pmap-fld"><span>KPI</span><TIn value={s.kpi} maxLength={120} onText={(v) => set((x) => { x.kpi = v; })} /></label>
            <label className="pmap-fld full"><span>Control: what must be true</span><TIn value={s.control} maxLength={160} onText={(v) => set((x) => { x.control = v; })} /></label>
          </>}
        </div>
        {s.type !== "end" && (s.link ? (
          <div className="pmap-link-ed">
            <a className="pmap-modlink" href={s.link.url} target="_blank" rel="noopener noreferrer"><span className="mono">{s.link.id || "Link"}</span><span>{s.link.label || s.link.url}</span><span aria-hidden="true">↗</span></a>
            {!ro && <div className="pmap-fields">
              <label className="pmap-fld"><span>Id</span><TIn value={s.link.id} maxLength={40} onText={(v) => set((x) => { if (x.link) x.link.id = v; })} /></label>
              <label className="pmap-fld"><span>Label</span><TIn value={s.link.label} maxLength={120} onText={(v) => set((x) => { if (x.link) x.link.label = v; })} /></label>
              <label className="pmap-fld full"><span>Link (https)</span><TIn value={s.link.url} maxLength={500} onText={(v) => set((x) => { if (x.link) x.link.url = v; })} /></label>
              <div><button className="btn pmap-sm" onClick={() => setNow((x) => { delete x.link; })}>Remove link</button></div>
            </div>}
          </div>
        ) : !ro && <div><button className="btn pmap-sm" onClick={() => setNow((x) => { x.link = { id: "", label: "", url: "https://" }; })}>Link a detailed instruction</button></div>)}
      </details>

      {s.type === "decision" ? (
        <Sec title="Branches" side={!ro && <button className="btn pmap-sm" onClick={() => ctx.addBranch(s.id)}>Add branch</button>}>
          {o.map((f) => (
            <div key={f.to} className="pmap-branch">
              <TIn value={f.label} maxLength={30} aria-label="Branch label" onText={(v) => ctx.editText((m) => { const x = m.flows.find((y) => y.from === f.from && y.to === f.to); if (x) x.label = v; })} />
              <button className="pmap-linkish to" onClick={() => ctx.select(f.to)}>→ {B[f.to]?.name}</button>
              {!ro && <button className="btn pmap-sm" aria-label={`Remove branch to ${B[f.to]?.name}`} onClick={() => ctx.removeFlow(f.from, f.to)}>Remove</button>}
            </div>))}
          {!ro && <label className="pmap-fld"><span>Branch to an existing step</span>
            <select value="" onChange={(e) => e.target.value && ctx.connect(s.id, e.target.value)}><option value="">Choose a step…</option>{options((id) => o.some((f) => f.to === id)).map((id) => <option key={id} value={id}>{B[id].name}</option>)}</select></label>}
        </Sec>
      ) : s.type !== "end" && (
        <Sec title="Next step">
          {ro ? <span>{o[0] ? <button className="pmap-linkish" onClick={() => ctx.select(o[0].to)}>{B[o[0].to]?.name}</button> : "None"}</span> : (
            <label className="pmap-fld"><span className="pmap-note">Drag the + handle on the map, or choose here.</span>
              <select value={o[0]?.to ?? ""} onChange={(e) => e.target.value && ctx.connect(s.id, e.target.value)}>{!o[0] && <option value="">Choose a step…</option>}{options(() => false).map((id) => <option key={id} value={id}>{B[id].name}</option>)}</select></label>)}
        </Sec>
      )}

      <Sec title={`Comments${cm.length ? ` (${cm.length})` : ""}`}>
        <div className="pmap-cmts">{cm.map((c) => (
          <div key={c.id} className="pmap-cmt-item"><div className="who">{c.byName ?? "Someone"}<span>{fmtDate(c.at)}</span>{c.mine && <button className="pmap-linkish" onClick={() => void del(c.id)}>Delete</button>}</div><p>{c.body}</p></div>))}</div>
        <textarea className="pmap-cmt" placeholder="Comment on this step: a question, a problem, a better way" aria-label="New comment" maxLength={1000} value={comment} onChange={(e) => setComment(e.target.value)} />
        <div><button className="btn pmap-sm" disabled={!comment.trim() || posting} onClick={() => void post()}>Add comment</button></div>
      </Sec>

      {!ro && (
        <Sec title="">
          <div className="btn-row">
            {s.type !== "end" && <button className="btn pmap-sm" onClick={() => ctx.addAfter(s.id)}>Add step after</button>}
            {(s.type === "task" || s.type === "start") && <button className="btn pmap-sm" onClick={() => ctx.addDecision(s.id)}>Add decision</button>}
            <button className="btn pmap-sm" onClick={() => { ctx.closeSheet(); ctx.startRename(s.id); }}>Rename on map</button>
            {s.type !== "start" && <button className="btn pmap-sm pmap-danger" onClick={() => ctx.removeStep(s.id)}>Delete</button>}
          </div>
        </Sec>
      )}
    </>
  );
}

function ScenarioPanel({ ctx, sc }: { ctx: PanelCtx; sc: Scenario }) {
  const { d, doc, B, ro, diff } = ctx;
  const [confirmDel, setConfirmDel] = useState(false);
  const p = pathOf(doc, sc);
  const flagged = new Set([...diff.added, ...Object.keys(diff.changed)]);
  const decisions = p.filter((id) => B[id]?.type === "decision" && outs(doc, id).length > 1);
  const set = (fn: (x: Scenario) => void) => ctx.editText((m) => { const x = m.scenarios.find((y) => y.id === sc.id); if (x) fn(x); });
  const steps = p.filter((id) => B[id]?.type !== "end");
  const byAgent = steps.filter((id) => B[id].type !== "start" && doneBy(doc, B[id]) === "agent").length;
  const laneOf = (id: string) => { const l = doc.lanes.find((x) => x.id === id); return l ? laneLabel(l) : "No lane"; };
  return (
    <>
      <div className="pmap-p-head">
        <div className="row"><span className="pmap-eyebrow">Scenario · {d.docNo}</span><button className="btn pmap-sm" onClick={() => ctx.setScen(null)}>Close</button></div>
        {ro ? <h2 className="pmap-h2">{sc.name}</h2> : <TIn required className="pmap-p-title" value={sc.name} maxLength={80} aria-label="Scenario name" onText={(v) => set((x) => { x.name = v; })} />}
      </div>
      <div className="pmap-fields">
        <label className="pmap-fld full"><span>Starts when</span><TIn value={sc.condition} maxLength={300} placeholder="e.g. Stock is short and the date allows the lead time" onText={(v) => set((x) => { x.condition = v; })} /></label>
      </div>
      {decisions.length > 0 && (
        <Sec title="Path">
          <span className="pmap-note">At each decision, the branch this scenario takes.</span>
          {decisions.map((id) => (
            <label key={id} className="pmap-fld pmap-choice"><span>{B[id].name}</span>
              <select value={outs(doc, id).find((f) => f.to === sc.choices[id])?.to ?? outs(doc, id)[0].to} disabled={ro} onChange={(e) => ctx.setChoice(sc.id, id, e.target.value)}>
                {outs(doc, id).map((f) => <option key={f.to} value={f.to}>{f.label ? `${f.label} → ` : "→ "}{B[f.to]?.name}</option>)}
              </select></label>))}
        </Sec>
      )}
      <Sec title="">
        <div className="btn-row"><button className="btn primary" onClick={() => ctx.startWalk(sc.id)}>Walk through</button></div>
      </Sec>
      <Sec title={`Steps · ${steps.length}`} side={byAgent ? <span className="pmap-wi-id">{byAgent} by an agent</span> : undefined}>
        <ol className="pmap-pathlist">{p.map((id) => (
          <li key={id}><button className="pmap-linkish" onClick={() => ctx.select(id)}>{B[id].name}</button><span className="ln">{laneOf(B[id].lane)}{flagged.has(id) && d.released && doc !== d.released ? <> · <span className="warn">changed</span></> : null}</span></li>))}</ol>
      </Sec>
      {!ro && (
        <Sec title="">
          {confirmDel ? <div className="btn-row"><span>Remove {sc.name}?</span><button className="btn pmap-danger" onClick={() => ctx.removeScenario(sc.id)}>Remove</button><button className="btn" onClick={() => setConfirmDel(false)}>Cancel</button></div>
            : <button className="btn pmap-sm pmap-danger" onClick={() => setConfirmDel(true)}>Remove scenario</button>}
        </Sec>
      )}
    </>
  );
}

function Outline({ ctx, active, sel, showDiff }: { ctx: PanelCtx; active: Active; sel: string | null; showDiff: ReturnType<typeof diffMaps> | null }) {
  const { doc, B, L, ro } = ctx;
  return (
    <div className="pmap-outline">
      <ol className="pmap-ol" aria-label="Steps in flow order">
        {L.ordered.map((id) => {
          const s = B[id]; if (!s) return null;
          const k = showDiff ? (showDiff.added.includes(id) ? "added" : showDiff.changed[id] ? "changed" : "") : "";
          return (
            <li key={id} className={`pmap-ol-row${sel === id ? " sel" : ""}${active && !active.nodes.has(id) ? " dim" : ""}`} onClick={(e) => { if (!(e.target as HTMLElement).matches("input,select")) ctx.select(id, { scroll: false }); }} onFocus={() => sel !== id && ctx.select(id, { scroll: false, sheet: false })}>
              <span className="n">{String(ctx.order(id)).padStart(2, "0")}</span>
              <span className={`t ${s.type}`} aria-hidden="true" />
              <TIn required id={`pmap-ol-${id}`} value={s.name} maxLength={120} aria-label={`Step ${ctx.order(id)} name`} onText={(v) => ctx.editText((m) => { const x = m.steps.find((y) => y.id === id); if (x) x.name = v; })} />
              <select className="lane" value={s.lane} disabled={ro} aria-label="Lane" onChange={(e) => ctx.moveLane(id, e.target.value)}>{doc.lanes.map((l) => <option key={l.id} value={l.id}>{laneLabel(l)}</option>)}</select>
              <span className="sys"><ScreenText s={s} /></span>
              <span className="end">{k && <span className={`pmap-tagx ${k}`}>{k === "added" ? "New" : "Changed"}</span>}
                <span className="dots" title="Scenarios that use this step">{doc.scenarios.map((sc) => <i key={sc.id} className={pathOf(doc, sc).includes(id) ? "on" : ""} />)}</span></span>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
